
import React, { useState, useRef, useEffect } from "react";
import stocai from "../../../../../../assets/PandaProfile.png";
import FutureIdentityCard from "../../Identity/FutureYou/FutureIdentityCard";
import { progressService } from "../../../../../../services/progressService";

const CURRENT_IDENTITY_SUBSECTION_ID = "f90552a1-12fd-4f0b-b4f5-17e258b84b97";

const Top3Values = ({ 
  userId,
  onFinish, 
  futureData = {},
  userName = "User",
  profileImage = null,
  initialValues = ["", "", ""] 
}) => {
  const [values, setValues] = useState(initialValues);
  const inputRefs = useRef([null, null, null]);
  const [currentIdentityProfileImage, setCurrentIdentityProfileImage] = useState(null);

  // ✅ FETCH PROFILE IMAGE FROM CURRENT IDENTITY (same as FutureIdentity.jsx)
  useEffect(() => {
    async function loadCurrentIdentity() {
      try {
        if (!userId || !CURRENT_IDENTITY_SUBSECTION_ID) return;
        
        const currentRec = await progressService.getUserProgress(
          userId,
          CURRENT_IDENTITY_SUBSECTION_ID
        );
        
        const currentPayload = currentRec?.data?.payload || currentRec?.data || {};

        if (currentPayload.profileImage) {
          setCurrentIdentityProfileImage(currentPayload.profileImage);
        }
      } catch (err) {
        console.error("Error loading current identity profile image:", err);
      }
    }

    loadCurrentIdentity();
  }, [userId]);

  // ✅ RESOLVE PROFILE IMAGE (priority: futureData > currentIdentity > prop > null)
  const resolvedProfileImage =
    futureData?.profileImage ||
    currentIdentityProfileImage ||
    profileImage ||
    null;

  // Focus input on mount
  useEffect(() => {
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, []);

  const handleInputChange = (index, value) => {
    const updatedValues = [...values];
    updatedValues[index] = value;
    setValues(updatedValues);
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (index < 2) {
        inputRefs.current[index + 1]?.focus();
      }
    } else if (e.key === "Tab") {
      e.preventDefault();
      if (index < 2) {
        inputRefs.current[index + 1]?.focus();
      }
    }
  };

  const handleFinish = () => {
    const filledValues = values.filter(v => v.trim() !== "");
    if (filledValues.length > 0) {
      onFinish?.(values);
    }
  };

  const isComplete = values.every(v => v.trim() !== "");

  return (
    <div className="w-full -mx-6 md:-mx-8">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        
        {/* LEFT SIDE - FORM (8 columns) */}
        <div className="lg:col-span-8 flex flex-col">
          <div className="bg-white rounded-2xl shadow-md flex-1 flex flex-col p-6 md:p-8">
            
            {/* Title */}
            <div style={{ marginBottom: "60px" }}>
              <h1
                style={{
                  fontFamily: "Gotham, sans-serif",
                  fontWeight: 500,
                  fontSize: 32,
                  color: "#323232",
                  lineHeight: "135%",
                  letterSpacing: "0%",
                  marginBottom: "12px",
                }}
              >
                Mapping Your Future Values
              </h1>
              
              {/* Divider Line */}
              <div
                style={{
                  borderBottom: "1.13px solid #F0F0F0",
                }}
              />
            </div>

            {/* Panda & Question Section */}
            <div className="flex items-start mt-12 gap-6">
                <img
                    src={stocai}
                    alt="Coach"
                    style={{
                    width: "100.640625px",
                    height: "100.640625px",
                    borderRadius: "50%",
                    flexShrink: 0,
                    }}
                />
                <div className="flex-1">
                    <h2
                    style={{
                        fontFamily: "Gotham, sans-serif",
                        fontWeight: 600,
                        fontSize: 24,
                        color: "#323232",
                        lineHeight: "1.35",
                        marginBottom: "8px",
                    }}
                    >
                    Which values will guide your future growth?
                    </h2>
                    <p
                    style={{
                        fontFamily: "Gotham, sans-serif",
                        fontWeight: 400,
                        fontSize: 14,
                        color: "#888888",
                        lineHeight: "1.65",
                    }}
                    >
                    Write down the three values that would matter for your future self.
                    </p>
                </div>
            </div>

            {/* Input Fields */}
            <div className="flex-1 ml-32 space-y-6 mb-8">
              {[0, 1, 2].map((index) => (
                <div
                  key={index}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    paddingLeft: "0px",
                    borderBottom: "1px solid #000",
                  }}
                >
                  {/* Plus Symbol - Fixed on line */}
                  <span
                    style={{
                      fontFamily: "Gotham, sans-serif",
                      fontWeight: 400,
                      fontSize: 20,
                      color: "#54B0AF",
                      lineHeight: "100%",
                      letterSpacing: "0%",
                      flexShrink: 0,
                      paddingBottom: "2px",
                    }}
                  >
                    +
                  </span>

                  {/* Input Field with Black Line */}
                  <input
                    type="text"
                    ref={(el) => (inputRefs.current[index] = el)}
                    value={values[index]}
                    onChange={(e) => handleInputChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(e, index)}
                    placeholder={`Future Value ${index + 1}`}
                    style={{
                      fontFamily: "Gotham, sans-serif",
                      fontWeight: 400,
                      fontSize: 20,
                      lineHeight: "100%",
                      letterSpacing: "0%",
                      color: "#323232",
                      padding: "8px 0",
                      border: "none",
                      borderBottom: "#000",
                      width: "100%",
                      outline: "none",
                      transition: "all 0.3s ease",
                      background: "transparent",
                      paddingLeft: "0px",
                    }}
                    onFocus={(e) => {
                      e.target.style.borderBottom = "#000";
                    }}
                    onBlur={(e) => {
                      e.target.style.borderBottom = " #000";
                    }}
                  />
                </div>
              ))}
            </div>

            {/* Button Section */}
            <div className="flex items-center gap-4 mt-4 pl-32">
              {/* Button */}
              <button
                onClick={handleFinish}
                disabled={!isComplete}
                style={{
                  width: 147,
                  height: 57,
                  borderRadius: 20,
                  background: isComplete ? "#0D1111" : "#CCCCCC",
                  fontFamily: "Gotham, sans-serif",
                  fontWeight: 500,
                  fontSize: 21,
                  color: "#FFFFFF",
                  border: "none",
                  cursor: isComplete ? "pointer" : "not-allowed",
                  transition: "all 0.3s ease",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  paddingTop: 22,
                  paddingRight: 20,
                  paddingBottom: 22,
                  paddingLeft: 20,
                  gap: 8,
                }}
                onMouseEnter={(e) => {
                  if (isComplete) e.currentTarget.style.background = "#1A1A1A";
                }}
                onMouseLeave={(e) => {
                  if (isComplete) e.currentTarget.style.background = "#0D1111";
                }}
              >
                <span
                  style={{
                    fontFamily: "Gotham, sans-serif",
                    fontWeight: 500,
                    fontSize: 21,
                    lineHeight: "100%",
                    letterSpacing: "0%",
                    color: "#FFFFFF",
                  }}
                >
                  Okay
                </span>

                {/* Icon Circle */}
                <div
                  style={{
                    width: 20,
                    height: 20,
                    background: "#FFFFFF",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <svg
                    width="12"
                    height="12"
                    fill="#0D1111"
                    viewBox="0 0 20 20"
                    style={{
                      transform: "rotate(0deg)",
                    }}
                  >
                    <path
                      fillRule="evenodd"
                      d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
              </button>

              {/* Press Enter Text */}
              <span
                style={{
                  fontFamily: "Gotham, sans-serif",
                  fontWeight: 700,
                  fontSize: 15,
                  lineHeight: "165%",
                  letterSpacing: "0%",
                  color: "#888888",
                }}
              >
                Press Enter ↵
              </span>
            </div>
          </div>
        </div>

        {/* RIGHT SIDE - FUTURE IDENTITY CARD (4 columns) */}
        <div className="lg:col-span-4 flex flex-col">
          <div className="flex-1">
            <FutureIdentityCard
              futureData={futureData}
              userName={userName}
              profileImage={resolvedProfileImage}
              currentInput=""
              coreValues={values.filter(v => v.trim() !== "")}
              showCoreValues={true}
            />
          </div>
        </div>

      </div>
    </div>
  );
};

export default Top3Values;

// import React, { useState, useRef, useEffect } from "react";
// import stocai from "../../../../../../assets/PandaProfile.png";
// import FutureIdentityCard from "../../Identity/FutureYou/FutureIdentityCard";

// const Top3Values = ({ 
//   onFinish, 
//   futureData = {},
//   userName = "User",
//   profileImage = null,
//   initialValues = ["", "", ""] 
// }) => {
//   const [values, setValues] = useState(initialValues);
//   const inputRefs = useRef([null, null, null]);

//   // Focus input on mount
//   useEffect(() => {
//     if (inputRefs.current[0]) {
//       inputRefs.current[0].focus();
//     }
//   }, []);

//   const handleInputChange = (index, value) => {
//     const updatedValues = [...values];
//     updatedValues[index] = value;
//     setValues(updatedValues);
//   };

//   const handleKeyDown = (e, index) => {
//     if (e.key === "Enter") {
//       e.preventDefault();
//       if (index < 2) {
//         inputRefs.current[index + 1]?.focus();
//       }
//     } else if (e.key === "Tab") {
//       e.preventDefault();
//       if (index < 2) {
//         inputRefs.current[index + 1]?.focus();
//       }
//     }
//   };

//   const handleFinish = () => {
//     const filledValues = values.filter(v => v.trim() !== "");
//     if (filledValues.length > 0) {
//       onFinish?.(values);
//     }
//   };

//   const isComplete = values.every(v => v.trim() !== ""); // ✅ ALL THREE must be filled

//   return (
//     <div className="w-full -mx-6 md:-mx-8">
//       <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        
//         {/* LEFT SIDE - FORM (8 columns) */}
//         <div className="lg:col-span-8 flex flex-col">
//           <div className="bg-white rounded-2xl shadow-md flex-1 flex flex-col p-6 md:p-8">
            
//             {/* Title */}
//             <div style={{ marginBottom: "60px" }}>
//               <h1
//                 style={{
//                   fontFamily: "Gotham, sans-serif",
//                   fontWeight: 500,
//                   fontSize: 32,
//                   color: "#323232",
//                   lineHeight: "135%",
//                   letterSpacing: "0%",
//                   marginBottom: "12px",
//                 }}
//               >
//                 Mapping Your Future Values
//               </h1>
              
//               {/* Divider Line */}
//               <div
//                 style={{
//                   borderBottom: "1.13px solid #F0F0F0",
//                 }}
//               />
//             </div>

//             {/* Panda & Question Section */}
//             <div className="flex items-start mt-12 gap-6">
//                 <img
//                     src={stocai}
//                     alt="Coach"
//                     style={{
//                     width: "100.640625px",
//                     height: "100.640625px",
//                     borderRadius: "50%",
//                     flexShrink: 0,
//                     }}
//                 />
//                 <div className="flex-1">
//                     <h2
//                     style={{
//                         fontFamily: "Gotham, sans-serif",
//                         fontWeight: 600,
//                         fontSize: 24,
//                         color: "#323232",
//                         lineHeight: "1.35",
//                         marginBottom: "8px",
//                     }}
//                     >
//                     Which values will guide your future growth?
//                     </h2>
//                     <p
//                     style={{
//                         fontFamily: "Gotham, sans-serif",
//                         fontWeight: 400,
//                         fontSize: 14,
//                         color: "#888888",
//                         lineHeight: "1.65",
//                     }}
//                     >
//                     Write down the three values that would matter for your future self.
//                     </p>
//                 </div>
//             </div>

//             {/* Input Fields */}
//             <div className="flex-1 ml-32 space-y-6 mb-8">
//               {[0, 1, 2].map((index) => (
//                 <div
//                   key={index}
//                   style={{
//                     display: "flex",
//                     alignItems: "center",
//                     gap: "12px",
//                     paddingLeft: "0px",
//                     borderBottom: "1px solid #000",
//                   }}
//                 >
//                   {/* Plus Symbol - Fixed on line */}
//                   <span
//                     style={{
//                       fontFamily: "Gotham, sans-serif",
//                       fontWeight: 400,
//                       fontSize: 20,
//                       color: "#54B0AF",
//                       lineHeight: "100%",
//                       letterSpacing: "0%",
//                       flexShrink: 0,
//                       paddingBottom: "2px",
//                     }}
//                   >
//                     +
//                   </span>

//                   {/* Input Field with Black Line */}
//                   <input
//                     type="text"
//                     ref={(el) => (inputRefs.current[index] = el)}
//                     value={values[index]}
//                     onChange={(e) => handleInputChange(index, e.target.value)}
//                     onKeyDown={(e) => handleKeyDown(e, index)}
//                     placeholder={`Future Value ${index + 1}`}
//                     style={{
//                       fontFamily: "Gotham, sans-serif",
//                       fontWeight: 400,
//                       fontSize: 20,
//                       lineHeight: "100%",
//                       letterSpacing: "0%",
//                       color: "#323232",
//                       padding: "8px 0",
//                       border: "none",
//                       borderBottom: "#000",
//                       width: "100%",
//                       outline: "none",
//                       transition: "all 0.3s ease",
//                       background: "transparent",
//                       paddingLeft: "0px",
//                     }}
//                     onFocus={(e) => {
//                       e.target.style.borderBottom = "#000";
//                     }}
//                     onBlur={(e) => {
//                       e.target.style.borderBottom = " #000";
//                     }}
//                   />
//                 </div>
//               ))}
//             </div>

//             {/* Button Section */}
//             <div className="flex items-center gap-4 mt-4 pl-32">
//               {/* Button */}
//               <button
//                 onClick={handleFinish}
//                 disabled={!isComplete}
//                 style={{
//                   width: 147,
//                   height: 57,
//                   borderRadius: 20,
//                   background: isComplete ? "#0D1111" : "#CCCCCC",
//                   fontFamily: "Gotham, sans-serif",
//                   fontWeight: 500,
//                   fontSize: 21,
//                   color: "#FFFFFF",
//                   border: "none",
//                   cursor: isComplete ? "pointer" : "not-allowed",
//                   transition: "all 0.3s ease",
//                   display: "flex",
//                   alignItems: "center",
//                   justifyContent: "space-between",
//                   paddingTop: 22,
//                   paddingRight: 20,
//                   paddingBottom: 22,
//                   paddingLeft: 20,
//                   gap: 8,
//                 }}
//                 onMouseEnter={(e) => {
//                   if (isComplete) e.currentTarget.style.background = "#1A1A1A";
//                 }}
//                 onMouseLeave={(e) => {
//                   if (isComplete) e.currentTarget.style.background = "#0D1111";
//                 }}
//               >
//                 <span
//                   style={{
//                     fontFamily: "Gotham, sans-serif",
//                     fontWeight: 500,
//                     fontSize: 21,
//                     lineHeight: "100%",
//                     letterSpacing: "0%",
//                     color: "#FFFFFF",
//                   }}
//                 >
//                   Okay
//                 </span>

//                 {/* Icon Circle */}
//                 <div
//                   style={{
//                     width: 20,
//                     height: 20,
//                     background: "#FFFFFF",
//                     borderRadius: "50%",
//                     display: "flex",
//                     alignItems: "center",
//                     justifyContent: "center",
//                     flexShrink: 0,
//                   }}
//                 >
//                   <svg
//                     width="12"
//                     height="12"
//                     fill="#0D1111"
//                     viewBox="0 0 20 20"
//                     style={{
//                       transform: "rotate(0deg)",
//                     }}
//                   >
//                     <path
//                       fillRule="evenodd"
//                       d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
//                       clipRule="evenodd"
//                     />
//                   </svg>
//                 </div>
//               </button>

//               {/* Press Enter Text */}
//               <span
//                 style={{
//                   fontFamily: "Gotham, sans-serif",
//                   fontWeight: 700,
//                   fontSize: 15,
//                   lineHeight: "165%",
//                   letterSpacing: "0%",
//                   color: "#888888",
//                 }}
//               >
//                 Press Enter ↵
//               </span>
//             </div>
//           </div>
//         </div>

//         {/* RIGHT SIDE - FUTURE IDENTITY CARD (4 columns) */}
//           <div className="lg:col-span-4 flex flex-col">
//             <div className="flex-1">
//               <FutureIdentityCard
//                 futureData={futureData}
//                 userName={userName}
//                 profileImage={futureData?.profileImage || profileImage}
//                 currentInput=""
//                 coreValues={values.filter(v => v.trim() !== "")}
//                 showCoreValues={true}
//               />
//             </div>
//           </div>

//       </div>
//     </div>
//   );
// };

// export default Top3Values;
